import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/shop';
import { usePageTitle } from '../a11y/usePageTitle';
import { brand, formatMoney } from '../brand/brand';
import { DealCard } from '../components/DealCard';
import { DealGallery } from '../components/DealGallery';
import { DealFaq } from '../components/DealFaq';
import { QuantityPicker } from '../components/QuantityPicker';
import { FavoriteButton } from '../components/FavoriteButton';
import type { Deal } from '../api/types';

/*
 * Deal detail, Direction B: gallery, title card, tabbed content, sticky
 * purchase rail, related deals.
 *
 * WHAT THE DESIGN ASKS FOR THAT THIS BUILD CANNOT HONESTLY SUPPLY
 *
 *   REVIEWS. The handoff specifies a third tab, "Reviews (312)", with a score,
 *   star breakdown and review cards. There is no review system — no table, no
 *   endpoint, nothing. The tab is omitted rather than shipped with invented
 *   reviews, because fabricated testimonials on a page asking for prepayment is
 *   not a placeholder, it is a lie with a number attached.
 *
 *   "182/200 sold". The API deliberately exposes stock as a BUCKET, never a
 *   count (see MarketplaceService: 'plenty' | 'limited' | 'last_few'), so that
 *   competitors cannot read a business's sales volume off the public page. The
 *   scarcity strip therefore says "Only a few left" with no numbers.
 *
 *   Merchant "Since 2023 · 4 live deals", location addresses and closing times,
 *   and the gallery thumbnails. None of these exist on the DTO. The sold-by card
 *   shows what is real; the gallery shows labelled placeholders, which is what
 *   the handoff says placeholders are for.
 *
 *   "Give as a gift" has no flow behind it (the handoff's own open question 2),
 *   so there is no button for it. A button that does nothing is worse than none.
 */

type Tab = 'details' | 'faq';

export function DealDetail() {
  const { dealId } = useParams<{ dealId: string }>();

  const [deal, setDeal] = useState<Deal | null>(null);
  const [related, setRelated] = useState<Deal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [tab, setTab] = useState<Tab>('details');

  // SOW §2.7. Kept OUT of the deal response on purpose — that one is publicly
  // cached for a minute, and "has this customer favourited it" is per-person, so
  // carrying it there would serve one shopper's state to the next.
  //
  // A SET rather than a boolean, because the business id is not known until the
  // deal itself arrives, and these two requests race.
  const [favoriteIds, setFavoriteIds] = useState<ReadonlySet<string>>(new Set());

  usePageTitle(deal?.title ?? null);

  useEffect(() => {
    if (dealId === undefined) return;

    let cancelled = false;

    // Reset per the handoff: quantity to 1, Details tab, top of page.
    setQuantity(1);
    setTab('details');
    setDeal(null);
    window.scrollTo({ top: 0 });

    api.deal(dealId)
      .then((result) => { if (!cancelled) setDeal(result); })
      .catch(() => { if (!cancelled) setError('That offer is no longer available.'); });

    // "More from this business" — filtered client-side because there is no
    // by-merchant endpoint. Fine at this catalogue size; revisit when the list
    // is long enough that fetching everything to show four is wasteful.
    api.deals()
      .then((all) => { if (!cancelled) setRelated(all); })
      .catch(() => { /* Related deals are a nicety; their absence is not an error. */ });

    // 401 for a signed-out shopper, which is the common case on a deal page and
    // not worth branching on — an empty set fills no hearts, which is exactly
    // right, and FavoriteButton renders nothing when signed out anyway.
    api.favorites()
      .then((list) => {
        if (!cancelled) setFavoriteIds(new Set(list.map((f) => f.businessId)));
      })
      .catch(() => { /* Signed out, or the call failed. Either way: not favourited. */ });

    return () => { cancelled = true; };
  }, [dealId]);

  const more = useMemo(
    () => (deal === null ? [] : related.filter((d) => d.businessName === deal.businessName && d.id !== deal.id)),
    [related, deal],
  );

  if (error !== null) {
    return (
      <div className="panel narrow" style={{ marginTop: 40 }}>
        <h2>Not available</h2>
        <p className="muted" style={{ margin: '8px 0 20px' }}>{error}</p>
        <Link to="/" className="btn">Browse other offers</Link>
      </div>
    );
  }

  if (deal === null) {
    return (
      <div className="detail" aria-busy="true">
        <div className="detail-main">
          <p className="visually-hidden" role="status">Loading offer…</p>
          <div className="skeleton" style={{ height: 320, borderRadius: 26 }} aria-hidden="true" />
          <div className="skeleton skeleton-line medium" aria-hidden="true" />
          <div className="skeleton skeleton-line" aria-hidden="true" />
        </div>
        <div className="skeleton" style={{ height: 420, borderRadius: 26 }} aria-hidden="true" />
      </div>
    );
  }

  const expires = new Date(deal.validTo);
  const expiresLong = expires.toLocaleDateString(brand.language, { dateStyle: 'long' });

  /* The meter fill is paid ÷ face value — the proportion of the credit the buyer
     is putting in. The remainder is the part they are getting for nothing, which
     is the whole proposition, so it is worth showing to scale rather than as a
     percentage nobody reads. */
  const paidShare = Math.round((deal.priceCents / deal.faceValueCents) * 100);

  return (
    <>
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/">← Back to deals</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/?category=${encodeURIComponent(deal.category)}`}>{deal.category}</Link>
        <span aria-hidden="true">/</span>
        <span className="current">{deal.title}</span>
      </nav>

      <div className="detail">
        <div className="detail-main">
          <DealGallery images={deal.images ?? []} fallbackUrl={deal.imageUrl} dealId={deal.id} category={deal.category} />

          <div className="title-card">
            <div className="stack-h wrap" style={{ gap: 10 }}>
              <span className="merchant-tile lg" aria-hidden="true" />
              <span style={{ font: '800 14.5px/1 var(--font-display)' }}>{deal.businessName}</span>
              <span className="verified">Verified partner</span>

              {/*
                SOW §2.7. Loaded separately from the deal, and that separation is
                required rather than tidy: the deal response is publicly cached
                for a minute, so "has THIS customer favourited it" cannot travel
                in it without serving one shopper's state to the next.
              */}
              <span className="push">
                <FavoriteButton
                  businessId={deal.businessId}
                  businessName={deal.businessName}
                  initial={favoriteIds.has(deal.businessId)}
                  onChange={(on) => setFavoriteIds((prev) => {
                    const next = new Set(prev);
                    if (on) next.add(deal.businessId); else next.delete(deal.businessId);
                    return next;
                  })}
                />
              </span>
            </div>

            <h1>{deal.title}</h1>

            <div className="assurances">
              <div className="assurance">
                <p className="t">Instant delivery</p>
                <p className="b">In My vouchers within seconds of paying.</p>
              </div>
              <div className="assurance">
                <p className="t">Refundable</p>
                <p className="b">Unused vouchers can be refunded in full, on request.</p>
              </div>
              <div className="assurance">
                <p className="t">Nothing to print</p>
                <p className="b">Show it on your phone at the counter.</p>
              </div>
            </div>
          </div>

          <div className="tabcard">
            <div className="tablist" role="tablist" aria-label="Deal information">
              <button
                type="button" role="tab" id="tab-details"
                aria-selected={tab === 'details'} aria-controls="panel-details"
                onClick={() => setTab('details')}
              >
                Details
              </button>
              <button
                type="button" role="tab" id="tab-faq"
                aria-selected={tab === 'faq'} aria-controls="panel-faq"
                onClick={() => setTab('faq')}
              >
                Questions
              </button>
            </div>

            {tab === 'details' && (
              <div className="tabpanel" role="tabpanel" id="panel-details" aria-labelledby="tab-details">
                <section className="stack" style={{ gap: 10 }}>
                  <h2>What you get</h2>
                  <p>
                    {deal.description ?? `${formatMoney(deal.faceValueCents)} of credit to spend with ${deal.businessName}.`}
                  </p>
                </section>

                <div className="specs">
                  <div className="spec">
                    <p className="k">You pay</p>
                    <p className="v">{formatMoney(deal.priceCents)}</p>
                  </div>
                  <div className="spec">
                    <p className="k">Credit</p>
                    <p className="v">{formatMoney(deal.faceValueCents)} toward your bill</p>
                  </div>
                  <div className="spec">
                    <p className="k">Valid until</p>
                    <p className="v">{expiresLong}</p>
                  </div>
                  <div className="spec">
                    <p className="k">Per customer</p>
                    <p className="v">{deal.maxPerUser === null ? 'No limit' : `Limit ${deal.maxPerUser}`}</p>
                  </div>
                </div>

                <section className="stack" style={{ gap: 10 }}>
                  <h2>Where to use it</h2>
                  <p>
                    {/* No rows means every branch — the API stores nothing for the
                        common case rather than a row per location. */}
                    {deal.locations.length === 0
                      ? `Any ${deal.businessName} location.`
                      : deal.locations.join(' · ')}
                  </p>
                </section>

                <section className="stack" style={{ gap: 10 }}>
                  <h2>Good to know</h2>
                  <div className="knowlist">
                    <p className="knowrow">Valid until {expiresLong}</p>
                    <p className="knowrow">Show it on your phone at the counter — nothing to print</p>
                    <p className="knowrow">Spend it in one visit or across several</p>
                    {deal.maxPerUser !== null && (
                      <p className="knowrow">Limit {deal.maxPerUser} per customer</p>
                    )}
                  </div>
                </section>

                {/*
                  THE MERCHANT'S OWN RESTRICTIONS, kept separate from "Good to
                  know" above — that list is ours and reads the same on every
                  deal. These are "dine-in only", "not valid Friday or Saturday",
                  "one per table", and until now they had nowhere to live: a rule
                  that existed only in the merchant's head became an argument at
                  the counter with the customer already holding a voucher.

                  Plain text split on newlines, never markup. The field is written
                  by a tenant and rendered to a customer.
                */}
                {deal.terms !== null && deal.terms.trim() !== '' && (
                  <section className="stack" style={{ gap: 10 }}>
                    <h2>{deal.businessName}&rsquo;s conditions</h2>
                    <ul className="termlist">
                      {deal.terms
                        .split('\n')
                        .map((line) => line.trim())
                        .filter((line) => line !== '')
                        .map((line, i) => <li key={i}>{line}</li>)}
                    </ul>
                  </section>
                )}
              </div>
            )}

            {tab === 'faq' && (
              <div className="tabpanel" role="tabpanel" id="panel-faq" aria-labelledby="tab-faq">
                <DealFaq deal={deal} />
              </div>
            )}
          </div>
        </div>

        <aside className="rail" aria-label="Buy this deal">
          <div className="buycard">
            <span className="microlabel">You pay</span>

            <div className="price-row" style={{ marginTop: -4 }}>
              <span className="price lg">{formatMoney(deal.priceCents)}</span>
              <s className="was" style={{ fontSize: 16 }}>
                <span className="visually-hidden">Worth </span>
                {formatMoney(deal.faceValueCents)}
              </s>
            </div>

            <span className="savepill">Save {formatMoney(deal.savingsCents)}</span>

            <div className="meter">
              <div className="row">
                <span>Credit at the counter</span>
                <span>{formatMoney(deal.faceValueCents)}</span>
              </div>

              {/* role="img" with a label: a bare pair of divs is announced as
                  nothing at all, and the bar IS the explanation of the offer. */}
              <div
                className="track"
                role="img"
                aria-label={`You pay ${formatMoney(deal.priceCents)} of ${formatMoney(deal.faceValueCents)} credit — ${100 - paidShare}% comes free`}
              >
                <div className="fill" style={{ width: `${paidShare}%` }} />
              </div>

              <div className="foot">
                <span>Your cost {formatMoney(deal.priceCents)}</span>
                <span className="keep">You keep {formatMoney(deal.savingsCents)}</span>
              </div>
            </div>

            <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              Spend it in one visit or across several — the balance is yours until it expires.
            </p>

            {deal.remaining === 'last_few' && <p className="scarcity">Only a few left</p>}

            <QuantityPicker value={quantity} max={deal.maxPerUser} onChange={setQuantity} />

            <Link to={`/checkout/${deal.id}?qty=${quantity}`} className="btn cta block">
              Buy for {formatMoney(deal.priceCents * quantity)}
            </Link>

            <p className="center" style={{ fontSize: 12.5, color: 'var(--c-text-faint)' }}>
              Valid until {expires.toLocaleDateString()} · Instant delivery
            </p>

            <div className="guarantees">
              <p className="guarantee">Unused vouchers refundable on request</p>
              <p className="guarantee">Card details never shared with the business</p>
              <p className="guarantee">Support replies {brand.supportReplyTime}</p>
            </div>
          </div>

          <div className="soldby">
            <span className="microlabel">Sold by</span>
            <div className="stack-h" style={{ gap: 12 }}>
              <span className="merchant-tile xl" aria-hidden="true" />
              <span style={{ font: '800 15.5px/1.2 var(--font-display)' }}>{deal.businessName}</span>
            </div>
            <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              {deal.businessName} sells this voucher and honours it at the counter.{' '}
              {brand.name} takes the payment and issues the credit.
            </p>
          </div>
        </aside>
      </div>

      {more.length > 0 && (
        <section className="section">
          <h2 style={{ fontSize: 22, marginBottom: 14 }}>More from {deal.businessName}</h2>
          <div className="grid compact">
            {more.map((d) => <DealCard key={d.id} deal={d} compact />)}
          </div>
        </section>
      )}
    </>
  );
}
